import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  FormHelperText,
} from '@mui/material';
import { PersonAdd, Save } from '@mui/icons-material';
import {
  CRM_LABELS,
  EMPTY_CUSTOMER_FORM,
  CUSTOMER_COMMAND_FIELDS,
  buildCreateCustomerCommand,
} from '../models/Client';
import {
  validateCustomerForm,
  parseCustomerApiErrors,
  translateApiFieldMessage,
  getChangedUpdateFields,
  normalizeNip,
} from '../validation/customerValidation';
import { crmService } from '../services/crmService';
import { mapGusResponseToForm, hasMappedGusData, parseGusApiError } from '../utils/gusData';
import GusFetchConfirmDialog from './GusFetchConfirmDialog';

const { STATUS_LABELS, PRIORITY_LABELS } = CRM_LABELS;

const pickEditFormData = (initialData) => {
  const form = { ...EMPTY_CUSTOMER_FORM };
  [...CUSTOMER_COMMAND_FIELDS, 'lastContactDate'].forEach((field) => {
    if (initialData && initialData[field] !== undefined && initialData[field] !== null) {
      form[field] = initialData[field];
    }
  });
  return form;
};

const CustomerFormDialog = ({
  open,
  onClose,
  onSubmit,
  isPending,
  mode = 'create',
  initialData = null,
}) => {
  const [form, setForm] = useState(EMPTY_CUSTOMER_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [gusConfirmOpen, setGusConfirmOpen] = useState(false);
  const [pendingGusNip, setPendingGusNip] = useState('');
  const [gusLoading, setGusLoading] = useState(false);
  const [gusError, setGusError] = useState('');
  const originalFormRef = useRef(null);
  const wasOpenRef = useRef(false);
  const declinedGusNipsRef = useRef(new Set());
  const fetchedGusNipsRef = useRef(new Set());
  const isEdit = mode === 'edit';

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    if (wasOpenRef.current) return;
    wasOpenRef.current = true;

    const nextForm = isEdit ? pickEditFormData(initialData) : { ...EMPTY_CUSTOMER_FORM };
    setForm(nextForm);
    originalFormRef.current = isEdit ? { ...nextForm } : null;
    setFieldErrors({});
    setGeneralError('');
    setGusConfirmOpen(false);
    setPendingGusNip('');
    setGusLoading(false);
    setGusError('');
    declinedGusNipsRef.current = new Set();
    fetchedGusNipsRef.current = new Set();
  }, [open, isEdit, initialData]);

  const maybePromptGusFetch = useCallback((nipValue) => {
    if (isEdit) return;

    const normalizedNip = normalizeNip(nipValue);
    if (normalizedNip.length !== 10) return;
    if (declinedGusNipsRef.current.has(normalizedNip)) return;
    if (fetchedGusNipsRef.current.has(normalizedNip)) return;

    setPendingGusNip(normalizedNip);
    setGusError('');
    setGusConfirmOpen(true);
  }, [isEdit]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setGeneralError('');
  };

  const handleNipChange = (e) => {
    handleChange('nip')(e);
    maybePromptGusFetch(e.target.value);
  };

  const handleDeclineGusFetch = () => {
    if (pendingGusNip) {
      declinedGusNipsRef.current.add(pendingGusNip);
      setForm((prev) => ({ ...prev, nip: pendingGusNip }));
    }
    setGusConfirmOpen(false);
    setGusError('');
    setPendingGusNip('');
  };

  const handleConfirmGusFetch = async () => {
    if (!pendingGusNip) return;

    setGusLoading(true);
    setGusError('');

    try {
      const gusData = await crmService.getGusCompanyByNip(pendingGusNip);
      const mapped = mapGusResponseToForm(gusData);

      if (!hasMappedGusData(mapped)) {
        setGusError('GUS nie zwrócił danych dla tego NIP-u. Sprawdź konfigurację integracji na backendzie.');
        return;
      }

      setForm((prev) => ({
        ...prev,
        nip: pendingGusNip,
        postalCode: mapped.postalCode || prev.postalCode,
        city: mapped.city || prev.city,
        address: mapped.address || prev.address,
      }));

      fetchedGusNipsRef.current.add(pendingGusNip);
      setGusConfirmOpen(false);
      setPendingGusNip('');
      setFieldErrors((prev) => {
        const next = { ...prev };
        ['postalCode', 'city', 'address', 'nip'].forEach((field) => {
          delete next[field];
        });
        return next;
      });
    } catch (error) {
      setGusError(parseGusApiError(error));
    } finally {
      setGusLoading(false);
    }
  };

  const getFieldError = (field) => fieldErrors[field] || '';

  const handleClose = () => {
    if (!isPending) {
      setForm(EMPTY_CUSTOMER_FORM);
      originalFormRef.current = null;
      wasOpenRef.current = false;
      setFieldErrors({});
      setGeneralError('');
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const changedFields = isEdit
      ? getChangedUpdateFields(form, originalFormRef.current || {})
      : null;
    const fieldsToValidate = isEdit ? Object.keys(changedFields) : null;

    const validation = await validateCustomerForm(form, {
      mode: isEdit ? 'edit' : 'create',
      fields: fieldsToValidate,
    });

    if (!validation.isValid) {
      setFieldErrors(validation.fieldErrors);
      setGeneralError(validation.generalError || '');
      return;
    }

    setFieldErrors({});
    setGeneralError('');

    try {
      const payload = isEdit ? changedFields : buildCreateCustomerCommand(form);
      await onSubmit(payload);
      setForm(EMPTY_CUSTOMER_FORM);
      originalFormRef.current = null;
      setFieldErrors({});
      setGeneralError('');
      onClose();
    } catch (submitError) {
      const { fieldErrors: apiFieldErrors, generalError: apiGeneralError } =
        parseCustomerApiErrors(submitError);

      const translatedErrors = Object.fromEntries(
        Object.entries(apiFieldErrors).map(([key, msg]) => [key, translateApiFieldMessage(msg)])
      );

      setFieldErrors(translatedErrors);
      setGeneralError(
        apiGeneralError
          ? translateApiFieldMessage(apiGeneralError)
          : Object.keys(translatedErrors).length
            ? ''
            : 'Nie udało się zapisać klienta.'
      );
    }
  };

  return (
    <>
    <GusFetchConfirmDialog
      open={gusConfirmOpen}
      nip={pendingGusNip}
      onConfirm={handleConfirmGusFetch}
      onDecline={handleDeclineGusFetch}
      isLoading={gusLoading}
      error={gusError}
    />
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isEdit ? 'Edytuj klienta' : 'Dodaj nowego klienta'}
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Nazwa firmy"
                value={form.companyName}
                onChange={handleChange('companyName')}
                size="small"
                error={Boolean(getFieldError('companyName'))}
                helperText={getFieldError('companyName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="NIP"
                value={form.nip}
                onChange={handleNipChange}
                size="small"
                placeholder="1234567890"
                error={Boolean(getFieldError('nip'))}
                helperText={getFieldError('nip') || '10 cyfr — po wpisaniu można pobrać dane z GUS'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Kod pocztowy"
                value={form.postalCode}
                onChange={handleChange('postalCode')}
                placeholder="00-000"
                size="small"
                error={Boolean(getFieldError('postalCode'))}
                helperText={getFieldError('postalCode')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Miasto"
                value={form.city}
                onChange={handleChange('city')}
                size="small"
                error={Boolean(getFieldError('city'))}
                helperText={getFieldError('city')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Ulica i numer"
                value={form.address}
                onChange={handleChange('address')}
                size="small"
                error={Boolean(getFieldError('address'))}
                helperText={getFieldError('address')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Telefon"
                value={form.phone}
                onChange={handleChange('phone')}
                size="small"
                error={Boolean(getFieldError('phone'))}
                helperText={getFieldError('phone')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                size="small"
                error={Boolean(getFieldError('email'))}
                helperText={getFieldError('email') || 'Opcjonalnie'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Osoba kontaktowa"
                value={form.contactPerson}
                onChange={handleChange('contactPerson')}
                size="small"
                error={Boolean(getFieldError('contactPerson'))}
                helperText={getFieldError('contactPerson') || 'Opcjonalnie'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={Boolean(getFieldError('status'))}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  label="Status"
                  onChange={handleChange('status')}
                >
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
                {getFieldError('status') && (
                  <FormHelperText>{getFieldError('status')}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={Boolean(getFieldError('priority'))}>
                <InputLabel>Priorytet</InputLabel>
                <Select
                  value={form.priority}
                  label="Priorytet"
                  onChange={handleChange('priority')}
                >
                  {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
                {getFieldError('priority') && (
                  <FormHelperText>{getFieldError('priority')}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notatki"
                value={form.notes}
                onChange={handleChange('notes')}
                size="small"
                error={Boolean(getFieldError('notes'))}
                helperText={getFieldError('notes')}
              />
            </Grid>
          </Grid>

          {generalError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {generalError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isPending}>
            Anuluj
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} /> : (isEdit ? <Save /> : <PersonAdd />)}
          >
            {isEdit ? 'Zapisz zmiany' : 'Dodaj klienta'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
    </>
  );
};

export default CustomerFormDialog;
